import { Component, EventEmitter, Input, OnInit, Output, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { Question } from 'src/app/core/models/questionnaire.model';
import { QuestionAnswer } from '../question/question.component';
import { CameraService } from '../document-kyc-upload-modal/services/camera.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-question-camera',
  templateUrl: './question-camera.component.html',
  styleUrls: ['./question-camera.component.scss'],
})
export class QuestionCameraComponent implements OnInit, OnDestroy {
  @Input() question!: Question;
  @Output() changeAnswer: EventEmitter<QuestionAnswer> = new EventEmitter();

  @ViewChild('videoElement', { static: false }) videoElement!: ElementRef<HTMLVideoElement>;

  isCameraActive = false;
  capturedPhoto: string | null = null;
  isLoading = false;
  facingMode: 'user' | 'environment' = 'environment';
  hasMultipleCameras = false;

  constructor(
    private cameraService: CameraService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.checkCameraAvailability();
    
    // Se já existe uma resposta, mostrar a foto capturada
    if (this.question.answer?.value && typeof this.question.answer.value === 'string') {
      this.capturedPhoto = this.question.answer.value;
    }
  }

  ngOnDestroy(): void {
    this.stopCamera();
  }

  async checkCameraAvailability(): Promise<void> {
    const capabilities = await this.cameraService.checkCameraAvailability();
    this.hasMultipleCameras = capabilities.cameras.length > 1;
  }

  async startCamera(): Promise<void> {
    try {
      this.isLoading = true;
      this.isCameraActive = true;
      
      // Aguarda o Angular renderizar o elemento de vídeo
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const stream = await this.cameraService.startCamera(this.facingMode);
      
      if (this.videoElement && this.videoElement.nativeElement) {
        this.videoElement.nativeElement.srcObject = stream;
        console.log('Camera stream set successfully');
      } else {
        console.error('Video element not found');
        this.isCameraActive = false;
      }
    } catch (error: any) {
      console.error('Error starting camera:', error);
      this.isCameraActive = false;
      this.snackBar.open(
        error.message || 'Failed to start camera. Please check permissions.',
        'Close',
        { duration: 5000 }
      );
    } finally {
      this.isLoading = false;
    }
  }

  stopCamera(): void {
    this.cameraService.stopCamera();
    this.isCameraActive = false;
  }

  async capturePhoto(): Promise<void> {
    try {
      if (!this.videoElement || !this.videoElement.nativeElement) {
        throw new Error('Video element not found');
      }

      this.isLoading = true;
      const blob = await this.cameraService.capturePhoto(
        this.videoElement.nativeElement,
        0.95
      );

      // Converter blob para base64
      const reader = new FileReader();
      reader.onloadend = () => {
        this.capturedPhoto = reader.result as string;
        this.stopCamera();
        this.emitAnswer(this.capturedPhoto);
      };
      reader.readAsDataURL(blob);
    } catch (error: any) {
      console.error('Error capturing photo:', error);
      this.snackBar.open(
        'Failed to capture photo. Please try again.',
        'Close',
        { duration: 3000 }
      );
    } finally {
      this.isLoading = false;
    }
  }

  async switchCamera(): Promise<void> {
    try {
      this.isLoading = true;
      const stream = await this.cameraService.switchCamera(this.facingMode);
      this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
      
      // Aguarda um momento para garantir que o elemento existe
      await new Promise(resolve => setTimeout(resolve, 50));
      
      if (this.videoElement && this.videoElement.nativeElement) {
        this.videoElement.nativeElement.srcObject = stream;
        console.log('Camera switched successfully');
      }
    } catch (error: any) {
      console.error('Error switching camera:', error);
      this.snackBar.open(
        'Failed to switch camera.',
        'Close',
        { duration: 3000 }
      );
    } finally {
      this.isLoading = false;
    }
  }

  retakePhoto(): void {
    this.capturedPhoto = null;
    this.emitAnswer(null);
    this.startCamera();
  }

  private emitAnswer(value: string | null): void {
    const answer: QuestionAnswer = {
      question: this.question,
      answer: {
        value: value,
        isIgnored: false,
      },
    };
    this.changeAnswer.emit(answer);
  }
}
